import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";

import DstAlert from "../../../components/DstAlert/DstAlert";
import DstButton from "../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../components/MultiSelectDropdown/MultiSelectDropdown";
import Popup from "../../../components/Popup/Popup";
import RequiredLabel from "../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../contexts/SessionContext";
import { EphemeralExercise } from "../../../models/EphemeralExercise";
import { validateUserPermission } from "../../../services/PermissionGuard";
import translate from "../../../services/Translate";
import { CursusOption, EphemeralCreationForm, EphemeralCreationModalProps } from "./CreationModal.d";
import {
  QCM_LANGUAGES,
  buildPayload,
  createEphemeralEvaluation,
  fetchCursus,
  fetchEphemeralExercises,
  getInitialForm,
  isFormInvalid,
} from "./CreationModal.function";

const EphemeralCreationModal = ({
  language,
  setShowCreationModal,
  showCreationModal,
  onCreated,
}: EphemeralCreationModalProps) => {
  const [loading, setLoading] = useState<{ creation: boolean; cursus: boolean; exercises: boolean }>({
    creation: false,
    cursus: false,
    exercises: false,
  });
  const { permissions } = useSession();
  const [form, setForm] = useState<EphemeralCreationForm>(getInitialForm());
  const [cursus, setCursus] = useState<CursusOption[]>([]);
  const [exercises, setExercises] = useState<EphemeralExercise[]>([]);
  const [selectedCursus, setSelectedCursus] = useState<CursusOption | null>(null);
  const [selectedExos, setSelectedExos] = useState<EphemeralExercise[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertProps, setAlertProps] = useState<any>({});

  const _resetSelection = () => {
    setForm(getInitialForm());
    setSelectedCursus(null);
    setSelectedExos([]);
  };

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const _handleTopicChange = (index: number, key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      topics_ratio: prev.topics_ratio.map((topic, topicIndex) =>
        topicIndex === index ? { ...topic, [key]: value } : topic
      ),
    }));
  };

  useEffect(() => {
    if (!showCreationModal) return;

    if (validateUserPermission({ methods: ["GET"], route: "/cursus" }, permissions)) {
      fetchCursus(language, setCursus, (value: boolean) => setLoading((prev) => ({ ...prev, cursus: value })));
    }
    if (validateUserPermission({ methods: ["GET"], route: "/ephemeral-evaluations/exercises" }, permissions)) {
      fetchEphemeralExercises(language, setExercises, (value: boolean) =>
        setLoading((prev) => ({ ...prev, exercises: value }))
      );
    }
  }, [language, permissions, showCreationModal]);

  return (
    <React.Fragment>
      <Popup
        show={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        modalTitle={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.TITLE")}
        size="xl"
        scrollable
      >
        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.NAME")} />
          <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "GLOBAL.TITLE")} />
          <Form.Control value={form.title} onChange={(event) => _handleChange("title", event.target.value)} />
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.DURATION")} />
          <Form.Control
            type="number"
            min={1}
            value={form.duration}
            onChange={(event) => _handleChange("duration", Number(event.target.value))}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.EXOS")} />
          <MultiSelectDropdown
            key={selectedExos.map((exercise) => exercise.ID).join("-")}
            items={exercises.map((exercise) => ({ ...exercise, id: exercise.ID }))}
            defaultSelectedItems={selectedExos.map((exercise) => ({ ...exercise, id: exercise.ID }))}
            language={language}
            mock={loading.exercises}
            badgeItemProperty="ID"
            displayItem={(item: EphemeralExercise) => (
              <span className="d-block text-wrap text-break">{item.resume}</span>
            )}
            label={
              selectedExos.length
                ? translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.PLACEHOLDERS.SELECTED_EXOS").replace(
                    "{count}",
                    String(selectedExos.length)
                  )
                : translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.PLACEHOLDERS.SELECT_EXOS")
            }
            onConfirm={(selected: EphemeralExercise[]) => {
              setSelectedExos(selected);
            }}
            borderToggle
            btnWidth="100%"
            toggleClass={`w-100 ${!selectedExos.length ? "text-muted" : ""}`}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <RequiredLabel label={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.MANDATORY_RATIO")} />
          <Form.Control
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={form.mandatory_ratio}
            onChange={(event) => _handleChange("mandatory_ratio", Number(event.target.value))}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          {translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.TOPICS_RATIO")}
          {form.topics_ratio.map((topic, index) => (
            <div key={index} className="d-flex align-items-center gap-2 mb-2">
              <Form.Control
                value={topic.name}
                placeholder={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.PLACEHOLDERS.TOPIC_NAME")}
                onChange={(event) => _handleTopicChange(index, "name", event.target.value)}
              />
              <Form.Control
                type="number"
                min={0}
                max={100}
                value={topic.percentage}
                placeholder={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.PLACEHOLDERS.TOPIC_PERCENTAGE")}
                onChange={(event) => _handleTopicChange(index, "percentage", event.target.value)}
              />
              <DstButton
                btnImageBefore={<span className="dst-icon-trash-01" />}
                variant="light"
                value=""
                clickFunction={() =>
                  setForm((prev) => ({
                    ...prev,
                    topics_ratio:
                      prev.topics_ratio.length === 1
                        ? [{ name: "", percentage: "" }]
                        : prev.topics_ratio.filter((_, i) => i !== index),
                  }))
                }
              />
            </div>
          ))}
          <DstButton
            value={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.BUTTONS.ADD_TOPIC")}
            btnImageBefore={<span className="me-2 dst-icon-plus" />}
            variant="light"
            clickFunction={() =>
              setForm((prev) => ({
                ...prev,
                topics_ratio: [...prev.topics_ratio, { name: "", percentage: "" }],
              }))
            }
          />
        </Form.Group>

        <Form.Group className="mb-3">
          {translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.CURSUS")}
          <MultiSelectDropdown
            items={cursus}
            language={language}
            mock={loading.cursus}
            isSingleSelect
            isSingleSelectRemovable
            badgeItemProperty="name"
            displayItem={(item: CursusOption) => <span>{item.name}</span>}
            label={
              selectedCursus?.name || translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.PLACEHOLDERS.SELECT_CURSUS")
            }
            onConfirm={(selected: CursusOption[]) => setSelectedCursus(selected.length ? selected[0] : null)}
            borderToggle
            btnWidth="100%"
            toggleClass="w-100"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          {translate(language, "GLOBAL.LANGUAGE")}
          <Form.Select value={form.language} onChange={(event) => _handleChange("language", event.target.value)}>
            {QCM_LANGUAGES.map(({ value, labelKey }) => (
              <option key={value} value={value}>
                {translate(language, `PAGES.EPHEMERAL.CREATION_MODAL.LANGUAGES.${labelKey}`)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          {translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.NOTES")}
          <Form.Control
            as="textarea"
            rows={2}
            value={form.notes}
            onChange={(event) => _handleChange("notes", event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          {translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.POOL_LENGTH")}
          <Form.Control
            type="number"
            min={0}
            value={form.pool_length}
            onChange={(event) => _handleChange("pool_length", event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            label={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.RANDOMIZE")}
            checked={form.randomize}
            onChange={(event) => _handleChange("randomize", event.target.checked)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            label={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.SEND_RESULT")}
            checked={form.send_result}
            onChange={(event) => _handleChange("send_result", event.target.checked)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            label={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.IS_DEPRECATED")}
            checked={form.is_deprecated}
            onChange={(event) => _handleChange("is_deprecated", event.target.checked)}
          />
        </Form.Group>

        <Modal.Footer>
          <DstButton
            value={translate(language, "GLOBAL.CANCEL")}
            variant="light"
            btnClass="me-2"
            clickFunction={() => {
              setShowCreationModal(false);
              _resetSelection();
            }}
          />
          <DstButton
            value={translate(language, "PAGES.EPHEMERAL.CREATION_MODAL.BUTTONS.CREATE")}
            variant="primary"
            loading={loading.creation}
            disabled={loading.creation || loading.cursus || loading.exercises || isFormInvalid(form, selectedExos)}
            clickFunction={() =>
              createEphemeralEvaluation(
                buildPayload(form, selectedExos, selectedCursus),
                language,
                (value: boolean) => setLoading((prev) => ({ ...prev, creation: value })),
                (props: any) => {
                  setAlertProps(props);
                  setShowAlert(true);
                },
                setShowCreationModal,
                setShowAlert,
                _resetSelection,
                onCreated
              )
            }
          />
        </Modal.Footer>
      </Popup>
      <DstAlert show={showAlert} onHide={() => setShowAlert(false)} {...alertProps} />
    </React.Fragment>
  );
};

export default EphemeralCreationModal;
